"use client";

import type { ColumnDef, ColumnOrderState, ColumnSizingState, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getColumnName } from "../column-helpers";

const HTML_TABLE_HEADER_TAG = "TH";

export interface UseColumnLayoutParams<TData> {
  columns: ColumnDef<TData>[];
  visibleColumnIds: string[] | null | undefined;
  stickyColumns: string[];
  hiddenColumns: string[];
  actionColumnIds: string[];
}

/** Видимость, ширина и порядок колонок, закрепление и перетаскивание. */
export function useColumnLayout<TData>({
  columns,
  visibleColumnIds,
  stickyColumns,
  hiddenColumns,
  actionColumnIds,
}: UseColumnLayoutParams<TData>) {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);

  const columnVisibility = useMemo<VisibilityState>(() => {
    if (visibleColumnIds === null || visibleColumnIds === undefined) {
      const vis0: VisibilityState = {};
      for (const id of actionColumnIds) vis0[id] = true;
      return vis0;
    }
    const allIds = columns
      .map((c) => getColumnName(c))
      .filter((id): id is string => typeof id === "string");
    const vis: VisibilityState = {};
    for (const id of allIds) {
      if (actionColumnIds.includes(id)) {
        vis[id] = true;
      } else {
        vis[id] = visibleColumnIds.includes(id);
      }
    }
    return vis;
  }, [columns, visibleColumnIds, actionColumnIds]);

  const allColumnIds = useMemo(
    () =>
      columns
        .map((c) => getColumnName(c))
        .filter((id): id is string => typeof id === "string"),
    [columns],
  );

  const stickyIds = useMemo(
    () => stickyColumns.filter((id) => allColumnIds.includes(id)),
    [stickyColumns, allColumnIds],
  );

  // Horizontal column freezing is done via JS `translateX` because
  // `position: sticky` on <th>/<td> is unreliable in Chromium when the
  // table is inside an overflow-x:auto container. `position: sticky; top:0`
  // is used only for the header row vertical stickiness.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);

  const applyStickyTransform = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;
    const stickyEls =
      container.querySelectorAll<HTMLElement>("[data-sticky-col]");
    stickyEls.forEach((el) => {
      const stickyLeft = Number(el.dataset["stickyLeft"] ?? "0");
      const offsetLeft = el.offsetLeft;
      const shiftX = scrollLeft - offsetLeft + stickyLeft;
      if (el.tagName === HTML_TABLE_HEADER_TAG) {
        // All header cells stay at the top of the scroll container.
        el.style.transform = `translate3d(${shiftX > 0 ? shiftX : 0}px, ${scrollTop}px, 0)`;
      } else {
        el.style.transform = shiftX > 0 ? `translateX(${shiftX}px)` : "";
      }
    });
    // Non-sticky header cells also need to stay at the top.
    const headerEls = container.querySelectorAll<HTMLElement>(
      ".smart-table thead th",
    );
    headerEls.forEach((el) => {
      if (el.dataset["stickyCol"] !== undefined) return;
      el.style.transform = `translateY(${scrollTop}px)`;
    });
  }, []);

  const handleTableScroll = useCallback(() => {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      applyStickyTransform();
    });
  }, [applyStickyTransform]);

  useEffect(() => {
    applyStickyTransform();
  }, [applyStickyTransform, stickyIds, columnOrder]);

  const hiddenIds = useMemo(
    () =>
      hiddenColumns.filter(
        (id) => allColumnIds.includes(id) && !stickyIds.includes(id),
      ),
    [hiddenColumns, allColumnIds, stickyIds],
  );

  const [localColumnVisibility, setLocalColumnVisibility] =
    useState<VisibilityState>({});

  useEffect(() => {
    setLocalColumnVisibility((prev) => {
      const base = { ...columnVisibility };
      for (const id of hiddenIds) {
        base[id] = false;
      }
      const prevOverridden: Record<string, boolean> = {};
      for (const id of Object.keys(prev)) {
        if (hiddenIds.includes(id) && prev[id] === true) {
          prevOverridden[id] = true;
        }
      }
      return { ...base, ...prevOverridden };
    });
  }, [columnVisibility, hiddenIds]);

  useEffect(() => {
    if (columnOrder.length === 0) {
      const ids = columns
        .map((c) => getColumnName(c))
        .filter((id): id is string => typeof id === "string");
      const ordered = [
        ...actionColumnIds.filter((id) => ids.includes(id)),
        ...stickyIds.filter(
          (id) => ids.includes(id) && !actionColumnIds.includes(id),
        ),
        ...ids.filter(
          (id) => !actionColumnIds.includes(id) && !stickyIds.includes(id),
        ),
      ];
      setColumnOrder(ordered);
    }
  }, [columns, columnOrder.length, stickyIds, actionColumnIds]);

  const handleColumnDrop = (targetId: string) => {
    if (!draggedColumn || draggedColumn === targetId) return;
    if (stickyIds.includes(draggedColumn) || stickyIds.includes(targetId))
      return;
    if (
      actionColumnIds.includes(draggedColumn) ||
      actionColumnIds.includes(targetId)
    )
      return;
    const newOrder = [...columnOrder];
    const fromIdx = newOrder.indexOf(draggedColumn);
    const toIdx = newOrder.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggedColumn);
    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  return { columnSizing, setColumnSizing, columnOrder, setColumnOrder, draggedColumn, setDraggedColumn, localColumnVisibility, setLocalColumnVisibility, columnVisibility, allColumnIds, stickyIds, scrollContainerRef, rafIdRef, applyStickyTransform, handleTableScroll, hiddenIds, handleColumnDrop };
}
